import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PackageConfigurator = ({ services }) => {
  const [selectedServices, setSelectedServices] = useState([]);
  const [packageName, setPackageName] = useState('');

  const toggleService = (serviceId) => {
    setSelectedServices(prev =>
      prev?.includes(serviceId)
        ? prev?.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const calculateTotal = () => {
    const total = selectedServices?.reduce((sum, id) => {
      const service = services?.find(s => s?.id === id);
      return sum + (service?.basePrice || 0);
    }, 0);

    const discount = selectedServices?.length >= 3 ? 0.15 : selectedServices?.length >= 2 ? 0.10 : 0;
    return {
      subtotal: total,
      discount: total * discount,
      total: total * (1 - discount),
      discountPercent: discount * 100
    };
  };

  const pricing = calculateTotal();

  return (
    <div className="bg-slate-800/40 backdrop-blur-xl border border-white/10 rounded-3xl p-4 md:p-6 shadow-2xl mb-6 md:mb-8">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-headline)' }}>
            Configurateur de forfait
          </h2>
          <p className="text-sm text-slate-400 mb-6">
            Créez votre forfait personnalisé et bénéficiez de réductions sur les services groupés
          </p>

          <div className="mb-6">
            <label className="block text-sm font-medium text-white mb-2">
              Nom du forfait (optionnel)
            </label>
            <input
              type="text"
              placeholder="Ex: Forfait Entreprise Premium"
              value={packageName}
              onChange={(e) => setPackageName(e?.target?.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-white/10 bg-slate-900/60 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/30 transition-all"
            />
          </div>

          <div className="space-y-3 mb-6">
            <h3 className="text-sm font-semibold text-white mb-3">Sélectionnez vos services</h3>
            {services?.map((service) => (
              <label
                key={service?.id}
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedServices?.includes(service?.id)
                    ? 'border-blue-500 bg-blue-500/5' :'border-white/10 hover:border-blue-500/50'
                }`}
              >
                <Checkbox
                  checked={selectedServices?.includes(service?.id)}
                  onChange={() => toggleService(service?.id)}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <Icon name={service?.icon} size={18} className="text-blue-400 flex-shrink-0" />
                      <span className="text-sm font-semibold text-white">{service?.title}</span>
                    </div>
                    <span className="text-sm font-bold text-blue-400 whitespace-nowrap">{service?.startingPrice}</span>
                  </div>
                  <p className="text-xs text-slate-400 line-clamp-1">{service?.description}</p>
                </div>
              </label>
            ))}
          </div>

          {selectedServices?.length >= 2 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-start gap-3"
            >
              <Icon name="Gift" size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-green-400 mb-1">
                  Réduction de {pricing?.discountPercent}% appliquée !
                </p>
                <p className="text-xs text-green-400/80">
                  {selectedServices?.length >= 3
                    ? 'Forfait Premium : 15% de réduction sur 3 services ou plus' :'Forfait Standard : 10% de réduction sur 2 services'}
                </p>
              </div>
            </motion.div>
          )}
        </div>

        <div className="lg:w-80">
          <div className="bg-slate-800/60 border border-white/10 rounded-xl p-6 sticky top-24">
            <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'var(--font-headline)' }}>
              Résumé du forfait
            </h3>

            {selectedServices?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Package" size={48} className="text-slate-500 mx-auto mb-3" />
                <p className="text-sm text-slate-400">
                  Sélectionnez des services pour créer votre forfait
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-4 pb-4 border-b border-white/10">
                  {selectedServices?.map((id) => {
                    const service = services?.find(s => s?.id === id);
                    return (
                      <div key={id} className="flex items-center justify-between gap-2">
                        <span className="text-sm text-white line-clamp-1">{service?.title}</span>
                        <span className="text-sm font-medium text-white whitespace-nowrap">{service?.startingPrice}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Sous-total</span>
                    <span className="text-sm font-medium text-white">{pricing?.subtotal?.toLocaleString()} CDF</span>
                  </div>
                  {pricing?.discount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-400">Réduction ({pricing?.discountPercent}%)</span>
                      <span className="text-sm font-medium text-green-400">-{pricing?.discount?.toLocaleString()} CDF</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-white/10">
                    <span className="text-base font-bold text-white">Total</span>
                    <span className="text-lg font-bold text-blue-400">{pricing?.total?.toLocaleString()} CDF</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Button
                    variant="default"
                    fullWidth
                    iconName="ShoppingCart"
                    iconPosition="left"
                  >
                    Commander le forfait
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Calendar"
                    iconPosition="left"
                  >
                    Consultation gratuite
                  </Button>
                </div>

                <p className="text-xs text-slate-400 text-center mt-4">
                  Prix estimatif. Devis détaillé après consultation.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageConfigurator;
